import * as angular from "angular";
import {ApiUrl} from "../../models/app-config.model";
import {CurrentUser} from "../../models/user/user.model";
import {UserService} from "../user/user.service";
import {Group} from "../../models/groups/group.model";
import {Instructor} from "../../models/instructor.model";
import {GroupProperties} from "../../models/groups/group-properties.model";
import {Student} from "../../models/student.model";

const ASSIGN_COMMAND = "ASSIGN_TESTPREP";
const MAXGRADE = 12;

const UPDATE_INSTRUCTORS_COMMAND = "UPDATE_INSTRUCTORS";
const UPDATE_STUDY_PROGRAMS_COMMAND = "UPDATE_STUDY_PROGRAMS";
const UPDATE_DEFAULT_GROUP_PREFERENCES = "UPDATE_DEFAULT_GROUP_PREFERENCES";
const REMOVE_GROUP_COMMAND = "REMOVE_GROUP";

export interface GroupsQuery {
    product: string;
    tenantId: string;
    groupId?: string;
    groupCode?: string;
    forProductAssignment?: boolean;
}

export interface GroupCommandProperties {
    districtManagedDefaultGroups: boolean;
    actPrepDefault: boolean;
    satPrepDefault: boolean;
}

export interface GroupsService {
    getOne: (groupId: string, tenantId?: string) => angular.IPromise<Group>;
    getAll: (tenantId?: string) => angular.IPromise<Group[]>;
    getForAssignment: (tenantId?: string) => angular.IPromise<Group[]>;
    calculateStudentGrade:(groupPromise: angular.IPromise<Group>) => angular.IPromise<Group>;
    assignGroups: (tenantId: string, properties: GroupProperties) => angular.IPromise<number>;
    deleteGroup: (tenantId: string, group: Group) => angular.IPromise<number>;

    updateInstructors: (tenantId: string, group: Group) => angular.IPromise<number>;
    updateStudyPrograms: (tenantId: string, group: Group) => angular.IPromise<number>;
    updateDefaultGroupPreferences: (tenantId: string, properties: GroupCommandProperties) => angular.IPromise<number>;
}

interface GroupsResponse { data: Group[];}
interface GroupResponse { data: Group; }

/* @ngInject */
export function groupsService(API_URL: ApiUrl, $http: angular.IHttpService, $q: angular.IQService,
                              userService: UserService, PRODUCT: string): GroupsService {

    let getURL = (groupsQuery?: GroupsQuery, groupId?: string) => {
        groupId = groupId ? `/${groupId}` : "";
        let groupsQueryStr = groupsQuery ? `?query=${JSON.stringify(groupsQuery)}` : "";
        return `${API_URL.groups.replace(/\/:groupId/, groupId)}${groupsQueryStr}`;
    };

    let makeInstructorDisplayName = (instructor: Instructor): void => {
        let name = instructor.name;
        let middle = name.middleName ? `${name.middleName} ` : "";
        instructor.name.displayName = `${name.firstName} ${middle}${name.lastName}`;
    };

    let buildGroup = (group: Group): void => {
        if (group.instructors && group.instructors.length > 0) {
            group.instructors.forEach(makeInstructorDisplayName);
        }
    };

    return {
        calculateStudentGrade: (groupPromise): angular.IPromise<Group> => {
            return groupPromise.then(function(group: Group) {
                let students = group.students;
                if(students && students.length > 0) {
                    students.forEach(function(student: Student) {
                        let studentClassYear = student.classYear;
                        let schoolYear = new Date();
                        schoolYear.setDate(schoolYear.getDate() + 180);
                        student.grade = MAXGRADE - (studentClassYear - schoolYear.getFullYear());
                    });
                }
                return group;
            });
        },
        getOne: (groupId: string, tenantId?: string): angular.IPromise<Group> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let groupsQuery: GroupsQuery = {
                    product: PRODUCT,
                    tenantId: tenantId || currentUser.tenantId
                };

                return $http.get(getURL(groupsQuery, groupId)).then((response: GroupResponse) => {
                    buildGroup(response.data);
                    return response.data;
                });
            });
        },
        getAll: (tenantId?: string): angular.IPromise<Group[]> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let groupsQuery:GroupsQuery = {
                    product: PRODUCT,
                    tenantId: tenantId || currentUser.tenantId
                };
                return $http.get(getURL(groupsQuery)).then((response:GroupsResponse) => {
                    response.data.forEach(buildGroup);
                    return response.data;
                }, (errorResponse) => {
                    if (errorResponse && errorResponse.status === 404) {
                        return [];
                    } else {
                        return $q.reject(errorResponse);
                    }
                });
            });
        },
        getForAssignment: (tenantId?: string): angular.IPromise<Group[]> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let groupsQuery:GroupsQuery = {
                    product: PRODUCT,
                    tenantId: tenantId || currentUser.tenantId
                };
                return $http.get(`${getURL(groupsQuery)}&forProductAssignment=true`).then((response:GroupsResponse) => {
                    response.data.forEach(buildGroup);
                    return response.data;
                });
            });
        },
        assignGroups: (tenantId: string, properties: GroupProperties): angular.IPromise<number> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let data = {
                    command: ASSIGN_COMMAND,
                    product: PRODUCT,
                    properties: properties,
                    tenantId: tenantId || currentUser.tenantId
                };
                let headers = {"Content-Type": "application/json"};
                return $http.post(`${getURL()}/group-command`, data, headers)
                    .then((response) => response.status);
            });
        },
        updateInstructors: (tenantId: string, group: Group):
            angular.IPromise<number> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let data = {
                    command: UPDATE_INSTRUCTORS_COMMAND,
                    product: PRODUCT,
                    navGroupId: group.groupId,
                    groupId: group.productGroups.TESTPREP.groupId,
                    tenantId: tenantId || currentUser.tenantId,
                    properties: {
                        instructors: group.instructors
                    }
                };

                let headers = {"Content-Type": "application/json"};
                return $http.post(`${getURL()}/group-command`, data, headers)
                    .then((response: {status: number}) => {
                        return response.status;
                    }, (errorResponse) => {
                        return errorResponse.status;
                    });
            });
        },
        updateStudyPrograms: (tenantId: string, group: Group):
            angular.IPromise<number> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let data = {
                    command: UPDATE_STUDY_PROGRAMS_COMMAND,
                    product: PRODUCT,
                    navGroupId: group.groupId,
                    groupId: group.productGroups.TESTPREP.groupId,
                    tenantId: tenantId || currentUser.tenantId,
                    properties: {
                        studyPrograms: group.productGroups.TESTPREP.studyPrograms
                    }
                };
                let headers = {"Content-Type": "application/json"};
                return $http.post(`${getURL()}/group-command`, data, headers)
                    .then((response: {status: number}) => {
                        return response.status;
                    }, (errorResponse) => {
                        return errorResponse.status;
                    });
            });
        },
        updateDefaultGroupPreferences: (tenantId: string, properties: GroupCommandProperties):
            angular.IPromise<number> => {
            let data = {
                command: UPDATE_DEFAULT_GROUP_PREFERENCES,
                product: PRODUCT,
                tenantId: tenantId,
                properties: properties
            };
            return $http.post(`${getURL()}/group-command`, data)
                .then((response: {status: number}) => response.status);
        },
        deleteGroup: (tenantId: string, group: Group): angular.IPromise<number> => {
            return userService.get().then((currentUser: CurrentUser) => {
                let data = {
                    command: REMOVE_GROUP_COMMAND,
                    product: PRODUCT,
                    navGroupId: group.groupId,
                    groupId: group.productGroups.TESTPREP.groupId,
                    tenantId: tenantId || currentUser.tenantId
                };

                let headers = {"Content-Type": "application/json"};
                return $http.post(`${getURL()}/group-command`, data, headers)
                    .then((response: {status: number}) => response.status);
            });
        }
    };
}


/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/common/groups/groups.service.ts
 **/